import React, { createContext, FC, ReactNode, useCallback, useContext, useEffect, useRef, useState, } from 'react'
import { classUtils as c } from "Vendor/Utils/ClassUtils";
import Api from "Vendor/Api";
import { UserContext } from "Vendor/Context/UserContextProvider";
import { BannerContext } from "Vendor/Context/BannerContextProvider";
import Import from "Entity/Import";
import { useTranslation } from "react-i18next";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "react-bootstrap"
import _FileCard from "../Components/Import/_FileCard";
import useHumanizeFileSizeUtils from "./Utils/HumanizeFileSizeUtils";
import Config from "../Entity/Import/Config";
import Bucket from "../Entity/Bucket";
import LoadingButton from "../Components/Button/LoadingButton";
import FormEmpty, { FormResponseType } from "./Components/FormEmpty";
import useTranslationDataUtils from "./Utils/TranslationDataUtils";
import _ImportModalTags from "../Components/Modal/_ImportModalTags";
import Tag from "../Entity/Tag";
import Collection from "../Config/Collection";
import { DataType } from "./Components/Form";
import useFormDataParser from "./Utils/FormDataParserUtils";
import CollectionImportModal from "../Components/Modal/CollectionImportModal";
import useBodyClass from "Hooks/useBodyClass";
import useActiveImport, { ImportState } from "Hooks/useActiveImport";
import EntityInterface from "./Definition/EntityInterface";
import Type from "../Config/Collection/Import/Column/Type";
import MatchedBy from "../Config/Collection/Import/Column/MatchedBy";
import Separator from "../Config/Collection/Import/Column/Separator";
import CollectionImport, { Group } from "Entity/Collection/Import";
import SearchableChoiceType, { Choice } from './Components/Form/Type/SearchableChoiceType';
import Product from 'Entity/Product';
import { importDroppedFiles } from './Utils/DroppedFiles';

type UploadSystemProps = {
    children: ReactNode
}

export const SupportedMimesTypes: { [key: string]: string } = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/gif': 'gif',
    'application/illustrator': 'ai',
    'image/vnd.adobe.photoshop': 'psd',
    'application/vnd.3gpp.pic-bw-small': 'psb',
    'image/tiff': 'tiff',
    'image/jp2': 'jp2',
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
    'video/avi': 'avi',
    'video/mp4': 'mp4',
    'video/x-flv': 'flv',
    'video/quicktime': 'mov',
    'video/x-m4v': 'm4v',
    'video/webm': 'webm',
    'application/zip': 'zip',
    'application/x-rar-compressed': 'rar',
    'application/gzip': 'gz',
    'application/x-tar': 'tar',
    'audio/mpeg': 'mp3',
    'application/eps': 'eps',
    'application/x-eps': 'eps',
    'image/eps': 'eps',
    'image/x-eps': 'eps',
    'image/heic': 'heic',
    'image/heif': 'heic',
    'application/vnd.adobe.indesign': 'indd',
    'application/vnd.adobe.indesign-idml-package': 'idml',
    'text/html': 'html',
}

export const ImageExtensions: { [key: string]: string } = {
    'png': 'image/png',
    'jpg': 'image/jpg',
}

export const mimeTypeImage = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/tiff', 'image/jp2']
export const mimeTypeVideo = ['video/avi', 'video/mp4', 'video/x-flv', 'video/quicktime', 'video/x-m4v', 'video/webm']
export const mimeTypeAudio = ['audio/mpeg', 'audio/mp3', 'audio/ogg', 'audio/wav', 'audio/x-wav', 'audio/x-m4a']
export const mimeTypeDocument = ['application/vnd.adobe.indesign', 'application/vnd.adobe.indesign-idml-package', 'application/pdf', 'application/msword', 'vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'text/html']
export const mimeTypeArchive = ['application/zip', 'application/x-rar-compressed', 'application/gzip', 'application/x-tar']
export const mimeTypeWithoutView = ['application/illustrator', 'image/vnd.adobe.photoshop']
export const SupportedImportDataMimesTypes = ['text/csv', 'text/plain', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']

export const iconByType = (type: string): string => {
    if (mimeTypeImage.includes(type)) {
        return 'fa-image'
    } else if (mimeTypeVideo.includes(type)) {
        return 'fa-video'
    } else if (mimeTypeAudio.includes(type)) {
        return 'fa-music'
    } else if (mimeTypeArchive.includes(type)) {
        return 'fa-archive'
    } else if (mimeTypeDocument.includes(type)) {
        if ('application/pdf' === type) {
            return 'fa-file-pdf'
        } else if ('application/msword' === type || 'vnd.openxmlformats-officedocument.wordprocessingml.document' === type) {
            return 'fa-file-word'
        } else if ('application/vnd.ms-excel' === type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' === type) {
            return 'fa-file-excel'
        } else if ('application/vnd.ms-powerpoint' === type || 'application/vnd.openxmlformats-officedocument.presentationml.presentation' === type) {
            return 'fa-file-powerpoint'
        } else if ('text/html' === type) {
            return 'fa-file-code'
        }
    }

    return 'fa-file'
}

type UploadSystemContextType = {
    openImportModalWithBucket: (bucket?: Bucket, product?: Product, onClose?: () => void) => void;
};

export const UploadSystemContext = createContext<UploadSystemContextType>({
    openImportModalWithBucket: () => { }
});

export type Queue = {
    [key: string]: UploadedFile
}

export type UploadedFileState =
    'pending'
    | 'presigned-url-requested'
    | 'presigned-url-not-found'
    | 'invalid-character-error'
    | 'ready'
    | 'uploading'
    | 'failed'
    | 'uploaded'

export type UploadedFileAnalyzeState = 'pending' | 'analyzing' | 'failed' | 'accepted' | 'rejected' | 'processing'

// AI vision POC: editable per-file suggestions for dynamic properties.
export type AiSuggestionKind = string
export type AiSuggestionStatus = 'suggested' | 'accepted' | 'rejected'

export type AiSuggestion = {
    property: string
    propertyId: number
    label: string
    kind: AiSuggestionKind
    value: string
    confidence: number | null
    status: AiSuggestionStatus
    source?: 'ai' | 'user'
    is_missing?: boolean
    create_choice?: boolean
}

export type AiChoiceProperty = {
    slug: string
    label: string
    values: string[]
    multiple?: boolean
}

// Keyed by AiSuggestionKind; null when the feature is disabled for the org.
export type AiChoices = {
    [kind in AiSuggestionKind]?: AiChoiceProperty
} | null

export type UploadedFile = {
    name: string,
    data: File,
    state: UploadedFileState
    uploaded: number
    size: number
    type: string
    presignedUrl?: string
    onRemove?: boolean
    previewUrl?: string
    analyzeStatus?: UploadedFileAnalyzeState
    newVersion?: number
    rejectReason?: number
    thumbnailUrl?: string
    aiSuggestions?: AiSuggestion[]
}

type AvailableCollection = {
    collection: Collection,
    id?: number,
    name: string
    entity?: EntityInterface
}

const createLocalPreviewUrl = (file: File, mimeType: string): string | undefined => {
    if (!mimeTypeImage.includes(mimeType)) {
        return undefined
    }

    return URL.createObjectURL(file)
}

const revokeLocalPreviewUrl = (previewUrl?: string) => {
    if (previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl)
    }
}

const isQueueReadyForDataImport = (queue?: Queue): boolean => {
    const files = Object.values(queue ?? {})

    return files.length === 1 && files[0].state === 'uploaded'
        && SupportedImportDataMimesTypes.includes(files[0].type)
}

const UploadSystem: FC<UploadSystemProps> = ({ children, }) => {
    const { t } = useTranslation()
    const { translate } = useTranslationDataUtils()
    const { formDataParser } = useFormDataParser()
    const { humanFileSize } = useHumanizeFileSizeUtils()
    const { currentUser, setCurrentUser } = useContext(UserContext)
    const { setBanner } = useContext(BannerContext)
    const [onDrag, setOnDrag] = useState<boolean>(false)
    const folderDropVersionRef = useRef(0)
    const [show, setShow] = useState<boolean>(false)
    const [showCancelModal, setShowCancelModal] = useState<boolean>(false)
    const [onCancel, setOnCancel] = useState<boolean>(false)
    const [notDroppableReason, setNotDroppableReason] = useState<string | undefined>(undefined)
    const [importState, setImportState] = useState<ImportState>('pending')
    const importContextRef = useRef({ currentUser, importState })
    importContextRef.current = { currentUser, importState }

    const {
        isProcessing,
        startActiveImport,
        clearActiveImport: resetActiveImport,
    } = useActiveImport({
        userId: currentUser?.id,
        onFinalized: () => {
            setImportState('success')
        },
        onFailed: () => {
            setImportState('error')
        },
        onError: () => {
            setImportState('error')
        },
    })

    useEffect(() => {
        if (isProcessing) {
            setImportState('processing')
        } else {
            // The hook also drops the lock without a callback: another tab finished it, TTL expiry, 404
            setImportState(state => (state === 'processing' ? 'pending' : state))
        }
    }, [isProcessing])

    const clearActiveImport = useCallback(() => {
        resetActiveImport()
        setImportState('pending')
    }, [resetActiveImport])

    const isDroppable = importState !== 'processing'
    const [queueSize, setQueueSize] = useState<number>(0)
    const [queueUploaded, setQueueUploaded] = useState<number>(0)
    const [queueUploadedPercent, setQueueUploadedPercent] = useState<number>(0)
    const [filesOnError, setFilesOnError] = useState<number>(0)
    const [sizeOnError, setSizeOnError] = useState<number>(0)
    const [availableCollection, setAvailableCollection] = useState<AvailableCollection | undefined>(undefined)
    const [tags, setTags] = useState<Tag[]>([])
    const [onChangeConfig, setOnChangeConfig] = useState<number | null>(null)
    const [bucket, setBucket] = useState<Bucket | undefined>(undefined)
    const [config, setConfig] = useState<Config | undefined>(undefined)
    const [aiChoices, setAiChoices] = useState<AiChoices>(null)
    const [importForm, setImportForm] = useState<boolean | FormResponseType>(true)
    const [availableBuckets, setAvailableBuckets] = useState<Bucket[]>([])
    const [availableCollections, setAvailableCollections] = useState<AvailableCollection[]>([])
    const [availableForDataImport, setAvailableForDataImport] = useState<boolean>(false)
    const [isValidable, setIsValidable] = useState<boolean>(false)
    const [isValidate, setIsValidate] = useState<boolean>(false)
    const [onValidate, setOnValidate] = useState<boolean>(false)
    const [onAnalyze, setOnAnalyze] = useState<boolean>(false)
    const [analyzable, setAnalyzable] = useState<boolean>(false)
    const [selectedSession, setSelectedSession] = useState<string>('');
    const [isSessionInputAvailable, setIsSessionInputAvailable] = useState(false);
    const [isRequired, setIsRequired] = useState(false);

    // Import Data
    const [showImportModal, setShowImportModal] = useState<boolean>(false)
    const [currentImport, setCurrentImport] = useState<CollectionImport | undefined>(undefined)
    const [onWaitStatus, setOnWaitStatus] = useState<boolean>(false)
    const [canDetail, setCanDetail] = useState<boolean>(false)
    const [canImport, setCanImport] = useState<boolean>(false)
    const [onRequest, setOnRequest] = useState<boolean>(false)
    const [importFailed, setImportFailed] = useState<boolean>(false)

    const [product, setProduct] = useState<Product | undefined>(undefined)

    const [onUploadModalClose, setOnUploadModalClose] = useState<(() => void) | null>(null);
    const previousBrandRef = useRef<string | undefined>(currentUser?.currentBrand)
    const activePresignedRequestsRef = useRef<Set<string>>(new Set())
    const activeUploadsRef = useRef<Set<string>>(new Set())
    const activeThumbnailPollsRef = useRef<Set<string>>(new Set())

    const showImportInProgress = useCallback(() => {
        setBanner({
            type: 'error',
            i18nTitle: 'app.warning',
            i18nMessage: 'app.importer.modal.processing.already_in_progress',
        })
    }, [setBanner])

    const openImportModalWithBucket = useCallback((
        selectedBucket?: Bucket,
        selectedProduct?: Product,
        onClose?: () => void
    ) => {
        if (importState === 'processing') {
            showImportInProgress();
            return;
        }
        setShow(true);
        if (selectedBucket) setBucket(selectedBucket);
        if (selectedProduct) setProduct(selectedProduct);
        if (onClose) setOnUploadModalClose(() => onClose());
    }, [importState, showImportInProgress]);

    const openImportModal = useCallback(() => {
        if (importState === 'processing') {
            showImportInProgress()
            return
        }
        setShow(true)
    }, [importState, showImportInProgress, setShow])

    const closeImportModal = useCallback(() => {
        setShow(false);
        if (onUploadModalClose) {
            onUploadModalClose();
            setOnUploadModalClose(null);
        }
    }, [setShow, onUploadModalClose]);

    const toggleUploadModal = useCallback(() => {
        if (importState === 'processing') {
            showImportInProgress();
            return;
        }
        if (showImportModal) {
            setShowImportModal(false)
            setCurrentImport(undefined)
            setAvailableCollection(undefined)

            return
        }

        if (show) {
            closeImportModal()
        } else {
            openImportModal()
        }
    }, [closeImportModal, importState, openImportModal, setBanner, show, showImportModal]);

    const onParamsChange = (currentImport?: CollectionImport) => {
        if (currentImport && ['uploaded', 'analyzed', 'wait-for-analyze', 'on-analyze'].includes(currentImport.status) && currentImport.uuid) {
            Api.post(`/collection/import/${currentImport.uuid}`, currentImport)
                .then(response => {
                    setImportFailed(false)
                    setCurrentImport({ ...response.data } as CollectionImport)
                }).catch(() => {
                    setImportFailed(true)
                }).then(() => {
                    setOnWaitStatus(false)
                })
        }
    }

    const onDetail = () => {
        if (currentImport && currentImport.uuid && canDetail) {
            let postData: DataType = {
                columns: [],
            }
            postData['columns'] = currentImport.columns.map(column => {
                let columnData: {
                    type: Type
                    bucket?: string
                    property?: number
                    language?: string
                    addressField?: string
                    matchedBy?: MatchedBy
                    formatDate?: string
                    propertyMatcher?: number
                    separator?: Separator
                } = {
                    type: column.type ?? 'ignored',
                    bucket: undefined,
                    property: undefined,
                    language: undefined,
                    addressField: undefined,
                    matchedBy: undefined,
                    formatDate: undefined,
                    propertyMatcher: undefined,
                    separator: undefined,
                }

                if (column.type === 'bucket') {
                    columnData.bucket = column.bucket
                } else if (column.type === 'property') {
                    columnData.property = column.property?.id

                    if (column.property?.type === 'address') {
                        columnData.addressField = column.addressField
                    }

                    if (column.property?.translate
                        || (
                            column.property?.type === 'address'
                            && column.addressField === 'country'
                            && column.matchedBy === 'name'
                        )
                        || (
                            column.property?.type === 'phone'
                        )
                    ) {
                        columnData.language = column.language
                    }

                    if (column.property &&
                        (column.property.type === 'collection'
                            || (column.property.type === 'address'
                                && column.addressField === 'country')
                        )
                    ) {
                        columnData.matchedBy = column.matchedBy
                    }

                    if ('date' === column.property?.type) {
                        columnData.formatDate = column.formatDate
                    }

                    if (column.separator) {
                        columnData.separator = column.separator;
                    }
                } else if (column.type === 'name'
                    && currentImport
                    && ['catalog', 'data-collection'].includes(currentImport.collection)
                ) {
                    columnData.language = column.language
                }

                if (column.type && ['bucket', 'mailingList'].includes(column.type)) {
                    columnData.matchedBy = column.matchedBy
                    columnData.separator = column.separator;
                }

                if (column.type === 'company') {
                    columnData.matchedBy = column.matchedBy;
                    if (currentUser?.currentOrganization?.setting.relationContactCompany === 'many') {
                        columnData.separator = column.separator;
                    }
                }

                if (column.type === 'group') {
                    columnData.matchedBy = column.matchedBy
                }

                if (columnData.matchedBy) {
                    columnData.propertyMatcher = column.propertyMatcher?.id
                }

                return columnData
            })
            setOnRequest(true)
            Api.post(`/collection/import/${currentImport.uuid}/analyze`, postData)
                .then(response => {
                    setImportFailed(false)
                    setCurrentImport({ ...response.data } as CollectionImport)
                }).catch(() => {
                    setImportFailed(true)
                }).then(() => {
                    setOnRequest(false)
                })
        }
    }

    const onImport = () => {
        if (currentImport && currentImport.uuid && canImport) {
            setOnRequest(true)
            Api.post(`/collection/import/${currentImport.uuid}/validate`)
                .then(response => {
                    setCurrentImport(undefined)
                    setShowImportModal(false)
                    setShow(false)
                    setAvailableCollection(undefined)
                    setCurrentUser(currentUser ? {
                        ...currentUser,
                        queue: undefined
                    } : undefined)
                }).catch(() => {
                    setImportFailed(true)
                }).then(() => {
                    setOnRequest(false)
                })
        }
    }

    // Use Effects

    useEffect(() => {
        let tempCanDetail = false
        let tempCanImport = false

        if (currentImport && currentImport.uuid) {
            if (currentImport.status === 'analyzed' || currentImport.status === 'detail') {
                tempCanDetail = currentImport.columns.every(column => {
                    return 'wait' !== column.state
                })
            }

            tempCanImport = currentImport.status === 'accepted'
        }

        setCanDetail(tempCanDetail)
        setCanImport(tempCanImport)
    }, [currentImport])

    useEffect(() => {
        if (availableCollection) {
            setShowImportModal(true)

            if (!currentImport) {
                setCurrentImport({
                    buckets: [],
                    collection: availableCollection.collection,
                    collectionId: availableCollection.id,
                    columns: [],
                    createdAt: "",
                    createdBy: undefined,
                    csvSeparator: undefined,
                    form: {
                        types: [],
                    },
                    id: 0,
                    ignoredFirstRow: true,
                    isCsv: false,
                    isGranted: undefined,
                    originalFileName: (Object.values(currentUser?.queue ?? {}).length) ? Object.values(currentUser?.queue ?? {})[0].name : '',
                    preview: [],
                    primarySlug: availableCollection.entity?.primarySlug,
                    rows: 0,
                    status: 'on-init',
                    updatedAt: "",
                    updatedBy: undefined,
                    uuid: ""
                })
                Api.get(`/collection/import/${availableCollection.collection}${availableCollection.entity?.primarySlug ? '/' + availableCollection.entity.primarySlug : ''}`)
                    .then(({ data }) => {
                        setImportFailed(false)
                        setCurrentImport({ ...data } as CollectionImport)
                    }).catch(() => {
                        setImportFailed(true)
                    }).then(() => {
                    })
            }
        } else {
            setShowImportModal(false)
        }
    }, [availableCollection, currentImport, currentUser?.queue])

    useBodyClass('showImportCollectionModal', showImportModal)

    useEffect(() => {
        if (currentImport && ['wait-for-analyze', 'on-analyze'].includes(currentImport.status) && !onWaitStatus) {
            const timer = setTimeout(() => {
                setOnWaitStatus(true)
                Api.get(`/collection/import/${currentImport.uuid}`)
                    .then(response => {
                        setImportFailed(false)
                        setCurrentImport({ ...response.data } as CollectionImport)
                    }).catch(() => {
                        setImportFailed(true)
                    }).then(() => {
                        setOnWaitStatus(false)
                    })
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [currentImport, onWaitStatus]);

    const reverseKeyValue = (obj: { [key: string]: string }): { [key: string]: string } => {
        const reversedObj: { [key: string]: string } = {};
        for (const key in obj) {
            const value = obj[key];
            reversedObj[value] = key;
        }

        return reversedObj;
    };

    const mapFileType = (fileType?: string) => {
        const reversedSupportedMimesTypes = reverseKeyValue(SupportedMimesTypes);

        if (!fileType) {
            return
        }
        const normalized = fileType === 'htm' ? 'html' : fileType;
        const mappedType = reversedSupportedMimesTypes[normalized];

        return mappedType ? mappedType : '';
    };

    const addFiles = (files: FileList | File[]) => {
        // Folder traversal is asynchronous: use the latest queue without replaying upload side effects
        // inside a React state updater, and ignore results if the import context changed.
        const { currentUser: user, importState: state } = importContextRef.current
        if (!user || state === 'processing' || user.id !== currentUser?.id || user.currentBrand !== currentUser?.currentBrand) return

        let tempQueue: Queue = { ...user.queue }
        for (let i = 0; i < files.length; i++) {
            let file = files[i]
            const sanitizedName = file.name.replace(/\p{C}/gu, '')
            const key = sanitizedName;
            let mimeType = file.type;
            activePresignedRequestsRef.current.delete(key)
            activeUploadsRef.current.delete(key)
            activeThumbnailPollsRef.current.delete(key)

            const extension = file.name.split(".").pop()?.toLowerCase();

            if (!mimeType) {
                mimeType = mapFileType(extension) || '';
            }

            revokeLocalPreviewUrl(tempQueue[key]?.previewUrl)

            tempQueue[key] = {
                data: file,
                state: 'pending',
                uploaded: 0,
                name: sanitizedName,
                size: file.size,
                type: mimeType,
                presignedUrl: undefined,
                previewUrl: createLocalPreviewUrl(file, mimeType),
            }
        }
        const nextUser = { ...user, queue: tempQueue }
        // Several folder callbacks can arrive before React renders the next queue state.
        importContextRef.current = { ...importContextRef.current, currentUser: nextUser }
        setCurrentUser(nextUser)
    }

    const cancelImport = useCallback(() => {
        folderDropVersionRef.current++
        setOnCancel(true)
        activePresignedRequestsRef.current.clear()
        activeUploadsRef.current.clear()
        activeThumbnailPollsRef.current.clear()
        clearActiveImport()
        Object.values(currentUser?.queue ?? {}).forEach(file => revokeLocalPreviewUrl(file.previewUrl))
        Api.delete('/upload/remove')
            .then(() => {
                setCurrentUser(currentUser ? {
                    ...currentUser,
                    queue: undefined,
                    currentImport: undefined,
                    currentCollectionImport: undefined
                } : undefined)
                setBucket(undefined)
                setConfig(undefined)
                setTags([])
                setShow(false)
                setShowCancelModal(false)
            }).catch(() => {
            })
            .then(() => {
                setOnCancel(false)
            })
    }, [currentUser, setCurrentUser, clearActiveImport])

    const deleteFile = useCallback((key: string) => {
        if (currentUser) {
            let tempQueue: Queue = { ...currentUser?.queue }
            activePresignedRequestsRef.current.delete(key)
            activeUploadsRef.current.delete(key)
            activeThumbnailPollsRef.current.delete(key)
            revokeLocalPreviewUrl(tempQueue[key]?.previewUrl)
            if (['failed', 'presigned-url-not-found', 'invalid-character-error'].includes(tempQueue[key].state)) {
                delete tempQueue[key]
                setCurrentUser({ ...currentUser, queue: tempQueue })
            } else {
                tempQueue[key].onRemove = true
                setCurrentUser({ ...currentUser, queue: tempQueue })
                Api.delete('/upload/remove', {
                    params: {
                        name: key,
                    }
                }).then(() => {
                    let tempQueue: Queue = { ...currentUser?.queue }
                    delete tempQueue[key]
                    setCurrentUser({ ...currentUser, queue: tempQueue })
                }).catch((error) => {
                    let tempQueue: Queue = { ...currentUser?.queue }
                    tempQueue[key].onRemove = false
                    setCurrentUser({ ...currentUser, queue: tempQueue })
                })
            }
        }
    }, [currentUser, setCurrentUser])

    const retry = useCallback((key: string) => {
        if (currentUser) {
            let tempQueue: Queue = { ...currentUser?.queue }
            activePresignedRequestsRef.current.delete(key)
            activeUploadsRef.current.delete(key)
            activeThumbnailPollsRef.current.delete(key)
            tempQueue[key].state = 'pending'
            tempQueue[key].uploaded = 0
            setCurrentUser({ ...currentUser, queue: tempQueue })
        }
    }, [currentUser, setCurrentUser])

    const retryAnalyze = useCallback((key: string) => {
        if (currentUser) {
            let tempQueue: Queue = { ...currentUser?.queue }
            tempQueue[key].analyzeStatus = 'pending'
            setCurrentUser({ ...currentUser, queue: tempQueue })
        }
    }, [currentUser, setCurrentUser])

    // AI vision POC: toggle (accept/reject) or upsert (add-missing / change) a
    // per-file suggestion. Kept entirely in local queue state and sent to the
    // backend at validate time (import files aren't persisted before then).
    const updateAiSuggestion = useCallback(
        (fileName: string, kind: AiSuggestionKind, payload: { status?: AiSuggestionStatus, value?: string, values?: string[], is_missing?: boolean, create_choice?: boolean }): Promise<void> => {
            setCurrentUser((prev) => {
                if (!prev?.queue?.[fileName]) {
                    return prev
                }

                const current = prev.queue[fileName].aiSuggestions ?? []
                let next: AiSuggestion[]
                const property = aiChoices?.[kind]

                if (payload.values !== undefined) {
                    const values = Array.from(new Set(payload.values.filter((value) => value !== '')))
                    const preserved = current.filter((s) => s.kind !== kind)
                    const entries: AiSuggestion[] = values.map((value) => ({
                        property: property?.slug ?? kind,
                        propertyId: 0,
                        label: property?.label ?? '',
                        kind,
                        value,
                        confidence: current.find((s) => s.kind === kind && s.value === value)?.confidence ?? null,
                        status: payload.status ?? 'accepted',
                        source: 'user',
                        is_missing: false,
                    }))
                    next = [...preserved, ...entries]
                } else if (payload.value !== undefined && payload.value !== '') {
                    const exists = current.some((s) => (s.kind === kind || s.property === kind) && s.value === payload.value)
                    if (payload.status === 'rejected') {
                        next = current.map((s) =>
                            (s.kind === kind || s.property === kind) && s.value === payload.value
                                ? { ...s, status: 'rejected' as AiSuggestionStatus }
                                : s,
                        )
                    } else if (exists) {
                        next = current.map((s) =>
                            (s.kind === kind || s.property === kind) && s.value === payload.value
                                ? {
                                      ...s,
                                      status: payload.status ?? 'accepted',
                                      is_missing: payload.is_missing ?? false,
                                      create_choice: payload.create_choice ?? true,
                                      source: 'user',
                                  }
                                : s,
                        )
                    } else {
                        const entry: AiSuggestion = {
                            property: property?.slug ?? kind,
                            propertyId: 0,
                            label: property?.label ?? '',
                            kind,
                            value: payload.value,
                            confidence: null,
                            status: payload.status ?? 'accepted',
                            source: 'user',
                            is_missing: payload.is_missing ?? false,
                            create_choice: payload.create_choice ?? true,
                        }
                        next = [...current, entry]
                    }
                } else if (payload.status !== undefined) {
                    next = current.map((s) =>
                        s.kind === kind ? { ...s, status: payload.status! } : s,
                    )
                } else {
                    return prev
                }

                const tempQueue: Queue = { ...prev.queue }
                tempQueue[fileName] = { ...tempQueue[fileName], aiSuggestions: next }

                return { ...prev, queue: tempQueue }
            })

            return Promise.resolve()
        },
        [aiChoices, setCurrentUser],
    )

    const changeConfig = useCallback((config: Config | null) => {
        if (config && config.id && bucket && currentUser) {
            setOnChangeConfig(config.id)
            Api.get('/upload/' + bucket.id + '/' + config.id + '/config')
                .then(({ data }: { data: { currentImport: Import, form: FormResponseType, analyzeQueue: Queue, aiChoices?: AiChoices } }) => {
                    setAiChoices(data.aiChoices ?? null)
                    let tempQueue = { ...currentUser.queue }
                    let analyzedKeys = Object.keys(data.analyzeQueue);
                    for (const [key, tempFile] of Object.entries(data.analyzeQueue)) {
                        if (tempQueue.hasOwnProperty(key)) {
                            tempQueue[key] = { ...tempQueue[key], ...tempFile }
                        }
                    }
                    for (const [key, tempFile] of Object.entries(tempQueue)) {
                        if (!analyzedKeys.includes(key)) {
                            let state = {
                                analyzeStatus: 'pending',
                                newVersion: undefined,
                                rejectReason: undefined,
                            }
                            tempQueue[key] = { ...tempFile, ...state } as UploadedFile
                        }
                    }
                    setImportForm(data.form)
                    setOnChangeConfig(null)
                    if (data.currentImport.config) {
                        setConfig({ ...data.currentImport.config })
                    }
                }).catch(error => {
                    setOnChangeConfig(null)
                    if (error?.response?.status === 409 || error?.response?.data?.error === 'import-in-progress') {
                        showImportInProgress()
                    }
                })
        } else {
            setOnChangeConfig(null)
            setConfig(undefined)
        }
    }, [bucket?.id, currentUser, setCurrentUser, showImportInProgress])

    const validateImport = useCallback(() => {
        let form = document.getElementById(process.env.REACT_APP_FORM_DATA_IMPORT_ID as string) as null | HTMLFormElement
        if (currentUser && bucket && config && config.id && form) {
            if (!form.checkValidity()) {
                form.reportValidity();
                return;
            }
            setOnValidate(true)
            let formData = new FormData(form)
            let data: DataType = {
                ...(config.tags ? { tags: tags } : {}),
                ...formDataParser(formData, typeof importForm === 'object' ? importForm.formName : '')
            }

            if (importState !== 'uploaded') {
                setCurrentUser({ ...currentUser, queue: { ...currentUser.queue } })
            }
            Api.patch('/upload/' + bucket.id + '/' + config.id + '/config', data).then((response) => {
                let data: { form: FormResponseType, currentImport: Import, session: string } = response.data
                setImportForm(data.form)
                setTags([...data.currentImport.tags])
                setSelectedSession(data.session);
                setIsValidate(true)
                setImportState('processing')
                setShow(false)
                if (onUploadModalClose) {
                    onUploadModalClose();
                    setOnUploadModalClose(null);
                }
            }).catch((error) => {
                setIsValidate(false)
            }).then(() => {
                setOnValidate(false)
            })
        }
    }, [bucket, config, currentUser, formDataParser, importForm, importState, setCurrentUser, tags, selectedSession, onUploadModalClose]);

    useEffect(() => {
        if (isValidate && (importState === 'uploaded' || importState === 'processing')) {
            const params = new URLSearchParams()
            if (product?.id) {
                params.set('product', String(product.id))
            }

            // AI vision POC: send the user-reviewed (non-rejected) suggestions per file
            // so the backend applies them on import. Kept compact: only kind/value/status.
            const aiMap: { [fileName: string]: { kind: AiSuggestionKind, value: string, status: AiSuggestionStatus }[] } = {}
            for (const [fileName, file] of Object.entries(currentUser?.queue ?? {})) {
                const active = (file.aiSuggestions ?? []).filter((s) => s.status !== 'rejected')
                if (active.length) {
                    aiMap[fileName] = active.map((s) => ({ kind: s.kind, value: s.value, status: s.status }))
                }
            }
            if (Object.keys(aiMap).length) {
                params.set('ai', JSON.stringify(aiMap))
            }

            const query = params.toString()
            const url = query ? `/upload/validate?${query}` : `/upload/validate`;

            Api.get(url).then((response) => {
                const importData: Import = response.data
                if (currentUser) {
                    Object.values(currentUser.queue ?? {}).forEach(file => revokeLocalPreviewUrl(file.previewUrl))
                    setCurrentUser({ ...currentUser, currentImport: undefined, queue: undefined })
                    setBucket(undefined)
                    setConfig(undefined)
                }
                if (importData?.uuid) {
                    startActiveImport(importData.uuid)
                    setImportState('processing')
                } else {
                    setImportState('success')
                }
            }).catch(() => {
                setImportState('error')
            }).then(() => {
                setIsValidate(false)
            }).finally(() => {
                setSelectedSession('')
                setProduct(undefined)
                setTags([])
            })
        }
    }, [isValidate, importState, currentUser, setCurrentUser, product?.id, startActiveImport])

    // Auto-dismiss completed state after 5 seconds
    useEffect(() => {
        if (importState === 'success') {
            const timer = setTimeout(() => {
                setImportState('pending')
            }, 5000)

            return () => clearTimeout(timer)
        }
    }, [importState])

    useEffect(() => {
        let tempAnalyzable = false;

        if (currentUser
            && bucket?.id
            && config?.id
            && currentUser.queue && Object.keys(currentUser.queue).length
        ) {
            for (const [key, file] of Object.entries(currentUser.queue)) {
                if (file.state === 'uploaded' && (!file.analyzeStatus || 'failed' !== file.analyzeStatus)) {
                    tempAnalyzable = true;
                    break;
                }
            }
        }

        setAnalyzable(tempAnalyzable)

    }, [currentUser, bucket, config])

    useEffect(() => {
        let tempValidable = false;

        if (currentUser
            && bucket?.id
            && config?.id
            && currentUser.queue
            && Object.keys(currentUser.queue).length
        ) {
            tempValidable = true;
            for (const [key, file] of Object.entries(currentUser.queue)) {
                if (file.state !== 'uploaded'
                    || !file.analyzeStatus
                    || ['pending', 'failed', 'analyzing', 'processing'].includes(file.analyzeStatus)
                ) {
                    tempValidable = false;
                    break;
                }
            }
        }

        setIsValidable(tempValidable)

    }, [bucket?.id, config?.id, currentUser, currentUser?.queue])

    useEffect(() => {
        setAvailableForDataImport(!!(
            !currentUser?.queue
            || !Object.keys(currentUser.queue).length
            || (
                Object.keys(currentUser.queue).length === 1
                && SupportedImportDataMimesTypes.includes(Object.values(currentUser.queue)[0].type)
            )
        ))
    }, [currentUser?.queue])

    useEffect(() => {
        let tempAvailableBuckets: Bucket[] = []
        const currentBrandSlug = currentUser?.currentBrand

        for (const [key, bucket] of Object.entries(currentUser?.currentOrganization?.buckets ?? {})) {
            if (!bucket.simpleImport && bucket.importConfigs?.length) {
                if (currentBrandSlug) {
                    if (bucket?.brand?.slug === currentBrandSlug) {
                        tempAvailableBuckets.push(bucket)
                    }
                } else {
                    tempAvailableBuckets.push(bucket)
                }
            }
        }

        setAvailableBuckets(tempAvailableBuckets)
    }, [currentUser?.currentBrand, currentUser?.currentOrganization?.buckets])

    useEffect(() => {
        const previousBrandSlug = previousBrandRef.current
        const currentBrandSlug = currentUser?.currentBrand

        if (previousBrandSlug !== currentBrandSlug) {
            setBucket(undefined)
            setConfig(undefined)
            setAvailableCollection(undefined)
            setCurrentImport(undefined)
            setShowImportModal(false)
        }

        previousBrandRef.current = currentBrandSlug
    }, [currentUser?.currentBrand])

    useEffect(() => {
        let tempAvailableCollections: AvailableCollection[] = []

        for (const [key, catalog] of Object.entries(currentUser?.currentOrganization?.catalogs ?? {})) {
            const currentBrandSlug = currentUser?.currentBrand
            if (currentBrandSlug) {
                if (catalog?.brand?.slug === currentBrandSlug) {
                    tempAvailableCollections.push({
                        collection: 'catalog',
                        id: catalog.id,
                        name: translate(catalog, 'name'),
                        entity: catalog,
                    })
                }
            } else {
                tempAvailableCollections.push({
                    collection: 'catalog',
                    id: catalog.id,
                    name: translate(catalog, 'name'),
                    entity: catalog,
                })
            }
        }

        for (const [key, dataCollection] of Object.entries(currentUser?.currentOrganization?.dataCollections ?? {})) {
            tempAvailableCollections.push({
                collection: 'data-collection',
                id: dataCollection.id,
                name: translate(dataCollection, 'name'),
                entity: dataCollection,
            })
        }

        tempAvailableCollections.push({
            collection: 'contact',
            name: t('app.contacts'),
        })

        tempAvailableCollections.push({
            collection: 'company',
            name: t('app.companies'),
        })

        if (currentUser?.currentOrganization?.options.includes('sales')) {
            tempAvailableCollections.push({
                collection: 'deal',
                name: t('app.deals'),
            })
        }

        setAvailableCollections(tempAvailableCollections)
    }, [currentUser?.currentBrand, currentUser?.currentOrganization, t, translate])

    useEffect(() => {
        if (importState === 'processing') {
            return
        }

        let size = 0
        let uploaded = 0
        let tempFilesOnError = 0
        let tempSizeOnError = 0
        for (const [key, uploadedFile] of Object.entries(currentUser?.queue ?? {})) {
            if (
                uploadedFile.state === 'failed' ||
                uploadedFile.state === 'presigned-url-not-found' ||
                uploadedFile.state === 'invalid-character-error'
            ) {
                tempFilesOnError++
                tempSizeOnError += uploadedFile.size
            } else {
                size += uploadedFile.size
                uploaded += uploadedFile.uploaded
            }
        }

        if (size === uploaded && size > 0) {
            setImportState('uploaded')
        } else if (uploaded > 0 && uploaded < size) {
            setImportState('uploading')
        } else if (size === 0) {
            if (importState !== 'success' && importState !== 'error') {
                setImportState('pending')
            }
            uploaded = 0
        }

        // round to 0 decimal
        setQueueUploadedPercent(size > 0 ? Math.round((uploaded / size) * 100) : 0)
        setQueueSize(size)
        setQueueUploaded(uploaded)
        setFilesOnError(tempFilesOnError)
        setSizeOnError(tempSizeOnError)
    }, [currentUser?.queue, importState])

    useBodyClass('on-drag', onDrag)

    const changeState = useCallback((keys: string[] | string, state: UploadedFileState, moreData: {} | null = null) => {
        setCurrentUser(prev => {
            if (!prev?.queue) {
                return prev
            }

            let tempQueue: Queue = { ...prev.queue }
            const queueKeys = typeof keys === 'string' ? [keys] : keys

            queueKeys.forEach(key => {
                if (tempQueue.hasOwnProperty(key)) {
                    let file = tempQueue[key];
                    file.state = state

                    if (null !== moreData && typeof moreData === 'object' && Object.keys(moreData).length) {
                        for (const [dataKey, value] of Object.entries(moreData)) {
                            // @ts-ignore
                            file[dataKey] = value
                        }
                    }
                }
            })

            return { ...prev, queue: tempQueue }
        })
    }, [setCurrentUser])

    const updateQueuedFile = useCallback((key: string, moreData: Partial<UploadedFile>) => {
        setCurrentUser(prev => {
            if (!prev?.queue?.[key]) {
                return prev
            }

            const tempQueue: Queue = { ...prev.queue }
            tempQueue[key] = { ...tempQueue[key], ...moreData }

            return { ...prev, queue: tempQueue }
        })
    }, [setCurrentUser])

    const ensureImportThumbnailIsQueued = useCallback(async (fileName: string): Promise<void> => {
        try {
            await Api.post('/upload/thumbnail', { file: fileName })
        } catch {
        }
    }, [])

    const queueImportThumbnail = useCallback(async (fileName: string): Promise<string | undefined> => {
        const checkReady = async (): Promise<string | undefined> => {
            const response = await Api.post('/upload/thumbnail', { file: fileName, checkOnly: true })
            if (response.data.ready && response.data.thumbnailUrl) {
                return response.data.thumbnailUrl
            }

            return undefined
        }

        const pollUntilReady = async (): Promise<string | undefined> => {
            for (let attempt = 0; attempt < 20; attempt++) {
                await new Promise(resolve => setTimeout(resolve, 3000))

                try {
                    const thumbnailUrl = await checkReady()
                    if (thumbnailUrl) {
                        return thumbnailUrl
                    }
                } catch {
                    return undefined
                }
            }

            return undefined
        }

        try {
            const response = await Api.post('/upload/thumbnail', { file: fileName, checkOnly: true })
            if (response.data.ready && response.data.thumbnailUrl) {
                return response.data.thumbnailUrl
            }

            return await pollUntilReady()
        } catch {
            return undefined
        }
    }, [])

    useEffect(() => {
        const maxFilesInQueue = 1
        if (currentUser) {
            var fileInQueue = 0
            for (const [key, uploadedFile] of Object.entries(currentUser?.queue ?? {})) {
                if (['presigned-url-requested', 'ready', 'uploading'].includes(uploadedFile.state)) {
                    fileInQueue++
                }
            }
            for (const [key, uploadedFile] of Object.entries(currentUser?.queue ?? {})) {
                if (uploadedFile.state === 'pending') {
                    if (fileInQueue < maxFilesInQueue) {
                        changeState(key, 'presigned-url-requested')
                        fileInQueue++

                        if (fileInQueue >= maxFilesInQueue) {
                            break;
                        }
                    }
                } else if (uploadedFile.state === 'presigned-url-requested' && !activePresignedRequestsRef.current.has(key)) {
                    activePresignedRequestsRef.current.add(key)
                    Api.get('/upload/presigned-url', {
                        params: {
                            name: uploadedFile.name,
                            size: uploadedFile.size,
                            type: uploadedFile.type,
                        }
                    }).then(response => {
                        changeState(key, 'ready', { presignedUrl: response.data.presignedUrl })

                    }).catch(error => {
                        const errorData = error?.response?.data?.error;

                        if ('invalid-character' === errorData) {
                            changeState(key, 'invalid-character-error', { presignedUrl: undefined })
                        } else if ('import-in-progress' === errorData || error?.response?.status === 409) {
                            changeState(key, 'presigned-url-not-found', { presignedUrl: undefined })
                            showImportInProgress()
                        } else {
                            changeState(key, 'presigned-url-not-found', { presignedUrl: undefined })
                        }
                    }).finally(() => {
                        activePresignedRequestsRef.current.delete(key)
                    })
                } else if (uploadedFile.state === 'ready' && !activeUploadsRef.current.has(key)) {
                    activeUploadsRef.current.add(key)
                    changeState(key, 'uploading')

                    if (uploadedFile.presignedUrl) {
                        let urlObject = new URL(uploadedFile.presignedUrl);
                        let uri = urlObject.pathname
                        let baseUrl = urlObject.origin
                        Api.put(uri, uploadedFile.data, {
                            params: Object.fromEntries(urlObject.searchParams),
                            baseURL: baseUrl,
                            withCredentials: true,
                            headers: {
                                'Content-Type': uploadedFile.type
                            },
                            onUploadProgress: (progressEvent) => {
                                updateQueuedFile(key, { uploaded: progressEvent.loaded })
                            }
                        }).then((response) => {
                            const thumbnailUrl = response.data.thumbnailUrl

                            changeState(key, 'uploaded', { uploaded: uploadedFile.size, thumbnailUrl })

                            if (!thumbnailUrl && !activeThumbnailPollsRef.current.has(key)) {
                                ensureImportThumbnailIsQueued(uploadedFile.name)
                            }
                        }).catch((error) => {
                            changeState(key, 'failed', { uploaded: uploadedFile.size })
                        }).finally(() => {
                            activeUploadsRef.current.delete(key)
                        })
                    } else {
                        activeUploadsRef.current.delete(key)
                        changeState(key, 'failed', { uploaded: uploadedFile.size })
                    }
                }
            }
        }
    }, [changeState, currentUser, ensureImportThumbnailIsQueued, showImportInProgress, updateQueuedFile])

    useEffect(() => {
        for (const [key, uploadedFile] of Object.entries(currentUser?.queue ?? {})) {
            if (
                uploadedFile.state === 'uploaded'
                && !uploadedFile.thumbnailUrl
                && mimeTypeImage.includes(uploadedFile.type)
                && !activeThumbnailPollsRef.current.has(key)
            ) {
                activeThumbnailPollsRef.current.add(key)

                queueImportThumbnail(uploadedFile.name).then((resolvedThumbnailUrl) => {
                    if (resolvedThumbnailUrl) {
                        updateQueuedFile(key, { thumbnailUrl: resolvedThumbnailUrl })
                    } else {
                        updateQueuedFile(key, { thumbnailUrl: 'failed' })
                    }
                }).finally(() => {
                    activeThumbnailPollsRef.current.delete(key)
                })
            }
        }
    }, [currentUser?.queue, queueImportThumbnail, updateQueuedFile])

    const updateConfig = async (tagsData: Tag[]): Promise<boolean> => {
        let form = document.getElementById(process.env.REACT_APP_FORM_DATA_IMPORT_ID as string) as null | HTMLFormElement;

        if (currentUser && bucket && config?.id && form) {
            if (!form.checkValidity()) {
                form.reportValidity();

                return false;
            }

            let formData = new FormData(form)
            let data: DataType = {
                ...(config.tags ? { tags: tagsData } : {}),
                ...formDataParser(formData, typeof importForm === 'object' ? importForm.formName : '')
            }

            try {
                const response = await Api.patch(`/upload/${bucket.id}/${config.id}/config`, data);
                const resData: { form: FormResponseType, currentImport: Import, session: string } = response.data;
                setImportForm(resData.form);
                setTags([...resData.currentImport.tags]);
                setSelectedSession(resData.session);

                return true;
            } catch (err) {
                console.error('Error updating config', err);

                return false;
            }
        }

        return false;
    };

    // Analyze
    const analyzeImport = useCallback(async () => {
        if (bucket?.id && config?.id && currentUser) {
            setOnAnalyze(true);

            if (bucket && bucket.sessionEnabled) {
                const configUpdated = await updateConfig(tags);

                if (!configUpdated) {
                    setOnAnalyze(false);

                    return;
                }
            }

            Api.get(`/upload/${bucket.id}/${config.id}/analyze`)
                .then(({ data }: { data: { analyzeQueue: Queue, aiChoices?: AiChoices, quotaExceeded?: boolean } }) => {
                    setAiChoices(data.aiChoices ?? null)
                    if (data.quotaExceeded) {
                        setBanner({
                            type: 'error',
                            i18nTitle: 'app.ai_quota_warning_title',
                            i18nMessage: 'app.ai_quota_warning_desc',
                        });
                    }
                    if (currentUser.queue) {
                        let tempQueue = { ...currentUser.queue }
                        let analyzedKeys = Object.keys(data.analyzeQueue);
                        for (const [key, tempFile] of Object.entries(data.analyzeQueue)) {
                            if (tempQueue.hasOwnProperty(key)) {
                                tempQueue[key] = { ...tempQueue[key], ...tempFile }
                            }
                        }
                        for (const [key, tempFile] of Object.entries(tempQueue)) {
                            if (!analyzedKeys.includes(key)) {
                                let state = {
                                    analyzeStatus: 'pending',
                                    newVersion: undefined,
                                    rejectReason: undefined,
                                }
                                tempQueue[key] = { ...tempFile, ...state } as UploadedFile
                            }
                        }
                        setCurrentUser({ ...currentUser, queue: tempQueue })
                    }
                })
                .catch(() => {
                })
                .then(() => {
                    setOnAnalyze(false)
                })
        }
    }, [bucket?.id, config?.id, currentUser, setCurrentUser, selectedSession, tags])


    // Background polling for AI vision processing on files in queue
    useEffect(() => {
        if (!currentUser?.queue || !bucket?.id || !config?.id || !show) {
            return
        }

        const hasProcessing = Object.values(currentUser.queue).some(
            (file) => file.analyzeStatus === 'processing'
        )

        if (!hasProcessing) {
            return
        }

        const timer = setInterval(() => {
            Api.get(`/upload/${bucket.id}/${config.id}/config`)
                .then(({ data }: { data: { analyzeQueue?: Queue, aiChoices?: AiChoices } }) => {
                    if (data?.analyzeQueue && currentUser?.queue) {
                        let hasChanged = false
                        const updatedQueue = { ...currentUser.queue }

                        for (const [key, serverFile] of Object.entries(data.analyzeQueue)) {
                            if (updatedQueue[key]) {
                                const currentFile = updatedQueue[key]
                                if (
                                    currentFile.analyzeStatus !== serverFile.analyzeStatus ||
                                    JSON.stringify(currentFile.aiSuggestions) !== JSON.stringify(serverFile.aiSuggestions)
                                ) {
                                    updatedQueue[key] = {
                                        ...currentFile,
                                        ...serverFile,
                                    }
                                    hasChanged = true
                                }
                            }
                        }

                        if (hasChanged) {
                            setCurrentUser({ ...currentUser, queue: updatedQueue })
                        }
                    }
                    if (data?.aiChoices) {
                        setAiChoices(data.aiChoices)
                    }
                })
                .catch(() => {
                    // Ignore transient network errors during background polling
                })
        }, 2000)

        return () => clearInterval(timer)
    }, [currentUser?.queue, bucket?.id, config?.id, show, setCurrentUser])

    const renameFile = useCallback(
        (oldName: string, newName: string): Promise<void> => {
            return new Promise((resolve, reject) => {
                if (currentUser) {
                    let tempQueue: Queue = { ...currentUser.queue }
                    if (tempQueue[oldName]) {
                        const oldFileData = tempQueue[oldName].data

                        //File data is readonly
                        const newFile = new File([oldFileData], newName, {
                            type: oldFileData.type,
                            lastModified: oldFileData.lastModified,
                        })

                        tempQueue[newName] = {
                            ...tempQueue[oldName],
                            name: newName,
                            data: newFile,
                        }

                        if (
                            tempQueue[oldName] &&
                            tempQueue[oldName].presignedUrl
                        ) {
                            const newPresignedUrl = tempQueue[
                                oldName
                            ].presignedUrl?.replace(oldName, newName)
                            tempQueue[newName].presignedUrl = newPresignedUrl
                        }

                        delete tempQueue[oldName]
                        setCurrentUser({ ...currentUser, queue: tempQueue })

                        Api.post('/upload/rename-file', { newName, oldName })
                            .then(() => {
                                setIsValidable(false)
                                resolve()
                            })
                            .catch((error) => {
                                reject(error)
                            })
                    } else {
                        reject(new Error('File not found in the queue'))
                    }
                } else {
                    reject(new Error('User not authenticated'))
                }
            })
        },
        [currentUser, setCurrentUser],
    )

    const validateSessionName = (value: string) => {
        const trimmed = value.trim();

        return trimmed.length >= 1 && !trimmed.includes('/');
    };

    const updateSessionInputWithObserver = useCallback((sessionValue: string | null) => {
        const updateInput = () => {
            const sessionInput = document.querySelector('#upload-modal [property="session" i] input') as HTMLInputElement | null;

            if (sessionInput) {
                sessionInput.value = sessionValue || '';
                const changeEvent = new Event('change', { bubbles: true });
                const inputEvent = new Event('input', { bubbles: true });
                sessionInput.dispatchEvent(changeEvent);
                sessionInput.dispatchEvent(inputEvent);

                return true;
            }

            setIsSessionInputAvailable(false);

            return false;
        };

        if (updateInput()) {
            return;
        };

        const observer = new MutationObserver((mutations) => {
            if (updateInput()) {
                observer.disconnect();
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        setTimeout(() => {
            observer.disconnect();
        }, 5000);
    }, []);

    useEffect(() => {
        if (selectedSession) {
            const sessionValue = selectedSession;
            updateSessionInputWithObserver(sessionValue);
        } else {
            updateSessionInputWithObserver('');
        }
    }, [selectedSession, updateSessionInputWithObserver]);

    const handleSessionChange = useCallback((newSession: string) => {
        setSelectedSession(newSession);
    }, []);

    useEffect(() => {
        const checkAvailability = () => {
            const input = document.querySelector('#upload-modal [property="session" i] input');
            setIsSessionInputAvailable(!!input);

            if (input) {
                setIsRequired(input.hasAttribute('required'));
            }
        };

        checkAvailability();
        const interval = setInterval(checkAvailability, 100);

        return () => clearInterval(interval);
    }, []);

    const showOnUpload = Boolean(
        Object.keys(currentUser?.queue ?? {}).length ||
        importState === 'processing' ||
        importState === 'success'
    )

    return (
        <UploadSystemContext.Provider value={{ openImportModalWithBucket }}>

            <div id="upload-system"
                onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation()
                    setOnDrag(true)
                }}

                onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation()
                    setOnDrag(true)
                }}

                onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation()
                    setOnDrag(false)
                }}

                onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation()
                    setOnDrag(false)
                    if (!isDroppable) {
                        showImportInProgress()
                        return
                    }
                    if (!e.dataTransfer.files.length && !Array.from(e.dataTransfer.items ?? []).some(item => item.kind === 'file')) return

                    const dropVersion = folderDropVersionRef.current
                    openImportModal()
                    importDroppedFiles(e.dataTransfer, files => {
                        if (dropVersion === folderDropVersionRef.current) addFiles(files)
                    })
                }}
            >
                {children}
                <Modal fullscreen show={show} onHide={closeImportModal} id="upload-modal">
                    <ModalHeader closeButton>
                        <Modal.Title>{t('app.import')}</Modal.Title>
                    </ModalHeader>
                    <ModalBody>
                        <div className="content">
                            <div
                                className={c('area', onDrag && 'onDrag', Object.keys(currentUser?.queue ?? {}).length === 0 ? 'showPlaceholder' : undefined)}
                                onDragEnter={() => {
                                    setOnDrag(true)
                                }}

                                onDragOver={() => {
                                    setOnDrag(true)
                                }}

                                onDragLeave={() => {
                                    setOnDrag(false)
                                }}

                                onDrop={() => {
                                    setOnDrag(false)
                                }}
                            >
                                <div className="row files">
                                    {Object.values(currentUser?.queue ?? {}).map((uploadedFile) => (
                                        <div className="col-3 mb-3" key={uploadedFile.name}>
                                            <_FileCard
                                                config={config}
                                                uploadedFile={uploadedFile}
                                                aiChoices={aiChoices}
                                                updateAiSuggestion={updateAiSuggestion}
                                                renameFile={renameFile}
                                                deleteFile={() => {
                                                    deleteFile(uploadedFile.name)
                                                }}
                                                retry={() => {
                                                    retry(uploadedFile.name)
                                                }}
                                                retryAnalyze={() => {
                                                    retryAnalyze(uploadedFile.name)
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="import-placeholder">
                                    <i className="fa-solid fa-folder-plus"></i>
                                    <span>{t('app.drop_your_files')}</span>
                                    <label htmlFor="import-input">{t('app.or_import')}</label>
                                    <input type="file" id="import-input" multiple onChange={(e) => {
                                        if (e.target.files && e.target.files.length) {
                                            addFiles(e.target.files)
                                        }
                                    }} />
                                </div>
                            </div>
                            <div className="form">
                                <div className="form-content">
                                    {(!bucket && !availableCollection)
                                        ? <div className="row justify-content-start align-items-center">
                                            {availableForDataImport && availableBuckets.length && availableCollections.length &&
                                                <div className="col-12 mb-3">
                                                    <h3 className="mb-0">{t('app.files_import')}</h3>
                                                </div>}
                                            {availableBuckets?.map((bucket, key) =>
                                                <div className="col-6 mb-2" key={key}>
                                                    <Button variant="secondary" className="btn-large btn-import"
                                                        onClick={() => {
                                                            setBucket(bucket)
                                                            setImportForm(true)
                                                        }}>{translate(bucket, 'name')}</Button>
                                                </div>
                                            )}
                                            {availableForDataImport && availableBuckets.length && availableCollections.length &&
                                                <div className="col-12 mb-3">
                                                    <h3 className="mb-0">{t('app.data_import')}</h3>
                                                </div>}
                                            {availableForDataImport && availableCollections?.map((availableCollection, key) =>
                                                <div className="col-6 mb-2" key={key}>
                                                    <Button
                                                        variant="secondary"
                                                        className="btn-large btn-import"
                                                        disabled={!isQueueReadyForDataImport(currentUser?.queue)}
                                                        onClick={() => {
                                                            setImportFailed(false)
                                                            setAvailableCollection(availableCollection)
                                                            // The panel opens below this window.
                                                            setShow(false)
                                                        }}>{availableCollection.name}</Button>
                                                </div>
                                            )}
                                        </div>
                                        : (bucket && !config
                                            ? <div className="row justify-content-start align-items-center">
                                                <div className="col-12 mb-3">
                                                    <div className="row justify-content-start align-items-center">
                                                        {((availableForDataImport && availableCollections.length) || availableBuckets.length > 1) &&
                                                            <div className="col-auto">
                                                                <Button variant="secondary" className="btn-icon"
                                                                    onClick={() => {
                                                                        setBucket(undefined)
                                                                    }}
                                                                ><i className="fa-solid fa-angle-left"></i></Button>
                                                            </div>}
                                                        <div className="col">
                                                            <h3 className="mb-0">{translate(bucket, 'name')}</h3>
                                                        </div>
                                                    </div>
                                                </div>
                                                {bucket.importConfigs?.map((config, key) =>
                                                    <div className="col-6 mb-2" key={key}>
                                                        <LoadingButton isLoading={onChangeConfig === config.id}
                                                            disabled={onChangeConfig !== null}
                                                            variant="secondary"
                                                            className="btn-large btn-import" onClick={() => {
                                                                changeConfig(config)
                                                            }}>{translate(config, 'name')}</LoadingButton>
                                                    </div>
                                                )}
                                            </div>
                                            : (config && bucket ? <>
                                                <div className="row align-items-center mb-3">
                                                    {bucket.importConfigs?.length > 1 ?
                                                        <div className="col-auto">
                                                            <Button
                                                                variant="secondary"
                                                                className="btn-icon"
                                                                onClick={() => {
                                                                    changeConfig(null)
                                                                }}
                                                            ><i className="fa-solid fa-angle-left"></i></Button>
                                                        </div>
                                                        : (availableBuckets.length > 1 ? <div className="col-auto">
                                                            <Button
                                                                variant="secondary"
                                                                className="btn-icon"
                                                                onClick={() => {
                                                                    setBucket(undefined)
                                                                }}
                                                            ><i className="fa-solid fa-angle-left"></i></Button>
                                                        </div>
                                                            : undefined)}
                                                    <div className="col-auto">
                                                        <h3 className="mb-0">{translate(config, 'name')}</h3>
                                                    </div>
                                                </div>
                                                <FormEmpty
                                                    path='/import/form'
                                                    delocateInitForm={importForm}
                                                    formId={process.env.REACT_APP_FORM_DATA_IMPORT_ID}
                                                    prependForm={
                                                        <>
                                                            {isSessionInputAvailable && <SearchableChoiceType
                                                                required={isRequired}
                                                                apiEndpoint={`/b/${bucket?.primarySlug}/sessions`}
                                                                value={selectedSession}
                                                                setValue={handleSessionChange}
                                                                maxDisplayedOptions={50}
                                                                showRemainingCount={true}
                                                                i18nLabel="app.session"
                                                                i18nPlaceholder="app.select_session"
                                                                i18nSearchPlaceholder=""
                                                                searchDelay={300}
                                                                i18nAddNewLabel="app.create_new_session"
                                                                allowAddNew={true}
                                                                addNewValidator={validateSessionName}
                                                                i18nAddNewInvalidMessage="app.session_slash_not_allowed"
                                                            />}
                                                        </>
                                                    }
                                                    appendForm={<>
                                                        {config.tags &&
                                                            <_ImportModalTags tags={tags} setTags={setTags}
                                                                onLoad={false}
                                                                className="bg-light p-3 rounded-1 border" />}</>}
                                                />
                                            </> : (availableCollection ?
                                                <>
                                                    <div className="row align-items-center mb-3">
                                                        <div className="col-auto">
                                                            <Button
                                                                variant="secondary"
                                                                className="btn-icon"
                                                                onClick={() => {
                                                                    setAvailableCollection(undefined)
                                                                }}
                                                            ><i className="fa-solid fa-angle-left"></i>
                                                            </Button>
                                                        </div>
                                                        <div className="col-auto">
                                                            <h3 className="mb-0">{t('app.import')}</h3>
                                                        </div>
                                                    </div>
                                                </>
                                                : <span
                                                    className="text-danger">{'Error. Bucket: ' + bucket?.name + ', Config: ' + config?.name + ', Data import :' + availableCollection}</span>)
                                            )
                                        )
                                    }
                                </div>
                                <div className="form-actions">
                                    <Button variant="secondary" onClick={() => {
                                        setShowCancelModal(true)
                                    }} disabled={onValidate}>{t('app.cancel')}</Button>
                                    {!isValidable && <LoadingButton variant="outline-primary" disabled={!analyzable}
                                        isLoading={onAnalyze}
                                        onClick={analyzeImport}>{t('app.analyze')}</LoadingButton>}
                                    {isValidable && <LoadingButton variant="primary" isLoading={onValidate}
                                        disabled={onValidate || !config || !isValidable}
                                        onClick={() => {
                                            validateImport()
                                        }}>{t('app.validate')}</LoadingButton>}
                                </div>
                            </div>
                        </div>
                    </ModalBody>
                </Modal>
                <Modal show={showCancelModal} onHide={() => {
                    setShowCancelModal(false)
                }}>
                    <ModalHeader closeButton>
                        <Modal.Title>{t('app.importer.modal.cancel.title')}</Modal.Title>
                    </ModalHeader>
                    <ModalBody>
                        <div className="content">
                            <div className="row justify-content-center align-items-center">
                                <div className="col-auto"><i className="fa-light fa-exclamation-circle text-danger"></i>
                                </div>
                                <div className="col">{t('app.importer.modal.cancel.content')}</div>
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="secondary" onClick={() => { setShowCancelModal(false) }}>{t('app.cancel')}</Button>
                        <LoadingButton variant="danger" isLoading={onCancel} onClick={cancelImport}>{t('app.importer.modal.cancel.confirm')}</LoadingButton>
                    </ModalFooter>
                </Modal>
                <CollectionImportModal
                    importFailed={importFailed}
                    collection={availableCollection?.collection ?? 'bucket'}
                    entity={availableCollection?.entity}
                    closeImportModal={() => {
                        setShowImportModal(false)
                        setShow(true)
                        setCurrentImport(undefined)
                        setAvailableCollection(undefined)
                    }}
                    currentImport={currentImport}
                    setCurrentImport={setCurrentImport}
                    onParamsChange={onParamsChange}
                    onDetail={onDetail}
                    onImport={onImport}
                    canDetail={canDetail}
                    canImport={canImport}
                    onRequest={onRequest}
                />
                <div id="upload-message"
                    className={c(
                        importState,
                        showOnUpload ? 'on-upload' : undefined,
                        isDroppable ? 'drop-possible' : 'drop-not-possible'
                    )}>
                    <div className="uploading" onClick={toggleUploadModal}>
                        <i className="fa-light fa-cloud-arrow-up me-2"></i>
                        {t('app.importer.modal.uploading.title')} - {humanFileSize(queueUploaded, true, 0)} / {humanFileSize(queueSize, true, 0)} - {queueUploadedPercent}%
                        {isValidate && <> - {t('app.importer.modal.uploading.is-validate')}</>}
                    </div>
                    <div className={c("uploaded", filesOnError > 0 && 'on-error')}
                        onClick={toggleUploadModal}>
                        <i className={c("fa-light me-2", filesOnError > 0 ? 'fa-cloud-exclamation' : 'fa-cloud-check')}></i>
                        {t('app.importer.modal.uploaded.staged')} - {humanFileSize(queueUploaded, true, 0)}
                        {' '}
                        ({Object.values(currentUser?.queue ?? {}).length})
                        {filesOnError > 0
                            ? (<> - {t('app.importer.modal.uploaded.error', { count: filesOnError })} ({humanFileSize(sizeOnError, true, 0)})</>)
                            : undefined
                        }
                    </div>
                    <div className="processing" onClick={showImportInProgress}>
                        <i className="fa-light fa-spinner fa-spin me-2"></i>
                        {t('app.importer.modal.processing.title')}
                    </div>
                    <div className="success"><i
                        className="fa-light fa-cloud-check me-2"></i>{t('app.importer.modal.uploaded.title')}</div>
                    <div className="error" onClick={toggleUploadModal}><i
                        className="fa-light fa-cloud-xmark me-2"></i>{t('app.importer.modal.error.title')}</div>
                    <div className="drop-possible"><i
                        className="fa-light fa-cloud-arrow-up me-2"></i>{t('app.importer.modal.drop-possible.title')}</div>
                    <div className="drop-not-possible"><i
                        className="fa-solid fa-hexagon-exclamation me-2"></i>{importState === 'processing'
                            ? t('app.importer.modal.processing.already_in_progress')
                            : t('app.importer.modal.drop-not-possible.title')}
                    </div>
                </div>
            </div>
        </UploadSystemContext.Provider>
    )
}

export default UploadSystem
